import { ThemeEvents, VariantUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * @typedef {Object} ShopifyVariant
 * @property {number} id
 * @property {number} price
 * @property {number | null} [compare_at_price]
 * @property {boolean} available
 * @property {string[]} [options]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {number} */
  #unitPriceCents = 0;
  /** @type {number} */
  #compareAtCents = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    if (this.#quantityPricingEnabled) {
      closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
      this.#unitPriceCents = Number(this.dataset.variantPrice) || 0;
      this.#compareAtCents = Number(this.dataset.variantCompareAt) || 0;
      this.#syncDisplay();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    closestSection.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  get #quantityPricingEnabled() {
    return this.dataset.quantityPricing === 'true';
  }

  /** @param {CustomEvent} event */
  #onQuantityChange = (event) => {
    if (!this.#quantityPricingEnabled || !(event instanceof QuantitySelectorUpdateEvent)) return;
    if (event.target.closest('cart-items-component')) return;
    const form = event.target.closest('product-form-component');
    if (!form || form.dataset.productId !== this.dataset.productId) return;
    this.#syncDisplay();
  };

  #getQuantity() {
    const form = this.closest('.shopify-section, dialog')?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`
    );
    const qty = form?.querySelector('quantity-selector-component');
    return qty ? Math.max(1, parseInt(/** @type {any} */ (qty).getValue?.() || '1', 10)) : 1;
  };

  #syncDisplay() {
    const qty = this.#quantityPricingEnabled ? this.#getQuantity() : 1;
    const price = this.#unitPriceCents * qty;
    const compareAt = this.#compareAtCents > this.#unitPriceCents ? this.#compareAtCents * qty : 0;
    this.#applyPriceDisplay(price, compareAt);
  }

  /** @param {number | undefined} variantId */
  #syncInstallments(variantId) {
    const input = /** @type {HTMLInputElement|null} */ (
      this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
    );
    if (input && variantId) {
      input.value = String(variantId);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Instantly updates price from cached variant data on selection.
   * @param {CustomEvent} event
   */
  #onVariantSelected = (event) => {
    const variant = event.detail?.resource?.variant;
    if (!variant) return;

    const variantPicker =
      event.target instanceof HTMLElement && event.target.tagName === 'VARIANT-PICKER'
        ? event.target
        : event.target instanceof HTMLElement
          ? event.target.closest('variant-picker')
          : null;

    if (
      variantPicker instanceof HTMLElement &&
      variantPicker.dataset.productId &&
      variantPicker.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    this.#setVariantPrices(/** @type {ShopifyVariant} */ (variant));
    this.#syncDisplay();
    this.#syncInstallments(variant.id);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const variant = /** @type {ShopifyVariant | undefined} */ (event.detail.resource);
    if (variant) {
      this.#setVariantPrices(variant);
      this.#syncDisplay();
    }
  };

  /** @param {ShopifyVariant} variant */
  #setVariantPrices(variant) {
    this.#unitPriceCents = variant.price ?? 0;
    this.#compareAtCents = variant.compare_at_price ?? 0;
    this.dataset.variantPrice = String(this.#unitPriceCents);
    this.dataset.variantCompareAt = String(this.#compareAtCents);
  }

  /**
   * @param {number} priceCents
   * @param {number} compareAtCents
   */
  #applyPriceDisplay(priceCents, compareAtCents) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    const regularBlock = priceContainer.querySelector('.price__regular');
    const saleBlock = priceContainer.querySelector('.price__sale');
    if (!regularBlock || !saleBlock) return;

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const onSale = compareAtCents > priceCents;
    const formattedPrice = formatMoney(priceCents, moneyFormat, currency);
    const formattedCompare = onSale ? formatMoney(compareAtCents, moneyFormat, currency) : '';

    if (onSale) {
      regularBlock.classList.add('price__hidden');
      saleBlock.classList.remove('price__hidden');
      saleBlock.querySelector('.price-item--sale')?.replaceChildren(document.createTextNode(formattedPrice));
      const compareEl = saleBlock.querySelector('.compare-at-price');
      if (compareEl) compareEl.textContent = formattedCompare;
    } else {
      regularBlock.classList.remove('price__hidden');
      saleBlock.classList.add('price__hidden');
      regularBlock.querySelector('.price')?.replaceChildren(document.createTextNode(formattedPrice));
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
